import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Lock, LogOut, CalendarClock, MapPin, Search, Download, BellRing, Check, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { fetchAdminAppointments, updateAppointmentStatus, triggerReminders } from "@/lib/api";
import { formatPrice } from "@/data/salon";

const KEY = "fabrowz_admin_pass";

export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [authPass, setAuthPass] = useState("");

  const load = async (pass) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminAppointments(pass);
      setRows(data);
      setAuthed(true);
      setAuthPass(pass);
      sessionStorage.setItem(KEY, pass);
    } catch (e) {
      setError("Incorrect passcode. Please try again.");
      setAuthed(false);
      sessionStorage.removeItem(KEY);
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await updateAppointmentStatus(id, status, authPass);
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked ${status}.`);
    } catch {
      toast.error("Could not update booking.");
    }
  };

  const sendReminders = async () => {
    try {
      const res = await triggerReminders(authPass);
      toast.success(res.sent > 0 ? `Sent ${res.sent} reminder(s) for tomorrow.` : "No appointments due for a reminder tomorrow.");
    } catch {
      toast.error("Could not send reminders.");
    }
  };

  const exportCsv = () => {
    const cols = ["Date", "Time", "Name", "Email", "Phone", "Treatment", "Branch", "Specialist", "Price", "Payment", "Status"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.join(",")];
    filtered.forEach((r) => {
      lines.push([r.date, r.time, r.name, r.email, r.phone, r.service_name, r.branch_name, r.specialist,
        `£${formatPrice(r.price || 0)}`, r.payment_status === "paid" ? "Paid online" : "Pay at salon", r.status || "confirmed"]
        .map(esc).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fabrowz-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const saved = sessionStorage.getItem(KEY);
    if (saved) load(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    sessionStorage.removeItem(KEY);
    setAuthed(false);
    setRows([]);
    setPasscode("");
  };

  const active = useMemo(() => rows.filter((r) => r.status !== "pending_payment"), [rows]);

  const filtered = useMemo(() => {
    if (!query.trim()) return active;
    const q = query.toLowerCase();
    return active.filter((r) =>
      [r.name, r.email, r.phone, r.service_name, r.branch_name]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [active, query]);

  const stats = useMemo(() => {
    const byBranch = {};
    let revenue = 0;
    let paid = 0;
    active.forEach((r) => {
      const b = r.branch_name || "Unspecified";
      byBranch[b] = (byBranch[b] || 0) + 1;
      revenue += r.price || 0;
      if (r.payment_status === "paid") paid += 1;
    });
    return { total: active.length, byBranch, revenue, paid };
  }, [active]);

  const paymentBadge = (r) => {
    if (r.payment_status === "paid")
      return { label: "Paid online", cls: "bg-green-100 text-green-700 border-green-200" };
    return { label: "Pay at salon", cls: "bg-pink/10 text-pink border-pink/20" };
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream px-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm rounded-3xl border border-pink/25 bg-surface p-8"
        >
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-pink/10">
            <Lock className="h-5 w-5 text-pink" />
          </div>
          <h1 className="text-center font-serif text-3xl text-ink">Fabrowz Admin</h1>
          <p className="mt-2 text-center text-sm text-muted">Enter your passcode to view bookings.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (passcode) load(passcode);
            }}
            className="mt-6 space-y-3"
          >
            <input
              type="password"
              data-testid="admin-passcode-input"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              className="w-full rounded-xl border border-pink/25 bg-cream px-4 py-3 text-sm text-ink placeholder:text-muted/60 focus:border-pink outline-none"
            />
            {error && <p data-testid="admin-error" className="text-xs text-red-600">{error}</p>}
            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full rounded-full bg-pink px-5 py-3 text-xs uppercase tracking-[0.2em] text-cream hover:bg-ink transition-colors disabled:opacity-50"
            >
              {loading ? "Checking…" : "Enter"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-pink/20 bg-cream/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-2xl text-pink font-semibold tracking-[0.15em]">FABROWZ</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Bookings</span>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 rounded-full border border-pink/30 px-4 py-2 text-xs uppercase tracking-[0.15em] text-ink hover:border-pink transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Log out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl border border-pink/20 bg-surface p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Total bookings</p>
            <p className="mt-2 font-serif text-4xl text-ink" data-testid="admin-total">{stats.total}</p>
          </div>
          <div className="rounded-2xl border border-pink/20 bg-surface p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Paid online</p>
            <p className="mt-2 font-serif text-4xl text-pink">{stats.paid}<span className="text-lg text-muted">/{stats.total}</span></p>
          </div>
          <div className="rounded-2xl border border-pink/20 bg-surface p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted mb-2">By branch</p>
            <div className="space-y-1">
              {Object.entries(stats.byBranch).map(([b, n]) => (
                <div key={b} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1 text-ink"><MapPin className="h-3 w-3 text-pink" />{b}</span>
                  <span className="font-mono text-muted">{n}</span>
                </div>
              ))}
              {stats.total === 0 && <p className="text-sm text-muted">No bookings yet.</p>}
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mb-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="relative max-w-sm flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, service, branch…"
              className="w-full rounded-full border border-pink/25 bg-surface pl-9 pr-4 py-2.5 text-sm text-ink placeholder:text-muted/60 focus:border-pink outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={sendReminders}
              data-testid="admin-send-reminders"
              className="flex items-center gap-2 rounded-full border border-pink/30 px-4 py-2.5 text-xs uppercase tracking-[0.15em] text-ink hover:border-pink transition-colors"
            >
              <BellRing className="h-3.5 w-3.5 text-pink" /> Send reminders
            </button>
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv"
              className="flex items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-xs uppercase tracking-[0.15em] text-cream hover:bg-inkhover transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-pink/20 bg-surface">
          <table className="w-full text-left text-sm" data-testid="admin-bookings-table">
            <thead>
              <tr className="border-b border-pink/15 font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
                <th className="px-4 py-3">Date / Time</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Treatment</th>
                <th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3">Specialist</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3 text-right">Price</th>
                <th className="px-4 py-3 text-right">Manage</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid="admin-booking-row" className="border-b border-pink/10 last:border-0 hover:bg-blush/40">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-ink"><CalendarClock className="h-3.5 w-3.5 text-pink" />{r.date}</div>
                    <div className="font-mono text-[11px] text-muted">{r.time}</div>
                  </td>
                  <td className="px-4 py-3 text-ink">{r.name}</td>
                  <td className="px-4 py-3">
                    <div className="text-ink">{r.email}</div>
                    <div className="font-mono text-[11px] text-muted">{r.phone}</div>
                    {r.notes ? <div className="text-[11px] text-muted italic mt-1 max-w-[200px]">“{r.notes}”</div> : null}
                  </td>
                  <td className="px-4 py-3 text-ink">{r.service_name}</td>
                  <td className="px-4 py-3 text-ink">{r.branch_name || "—"}</td>
                  <td className="px-4 py-3 text-muted">{r.specialist}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.1em] ${paymentBadge(r).cls}`}>
                      {paymentBadge(r).label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-serif text-lg text-pink">{r.from_price ? "from " : ""}£{formatPrice(r.price || 0)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {(r.status || "confirmed") === "cancelled" ? (
                        <span className="rounded-full bg-red-100 text-red-600 border border-red-200 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.1em]">Cancelled</span>
                      ) : (r.status || "confirmed") === "done" ? (
                        <span className="rounded-full bg-green-100 text-green-700 border border-green-200 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.1em]">Done</span>
                      ) : (
                        <>
                          <button
                            onClick={() => setStatus(r.id, "done")}
                            data-testid={`admin-done-${r.id}`}
                            title="Mark as done"
                            className="rounded-full border border-green-300 p-1.5 text-green-600 hover:bg-green-50"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setStatus(r.id, "cancelled")}
                            data-testid={`admin-cancel-${r.id}`}
                            title="Cancel booking"
                            className="rounded-full border border-red-300 p-1.5 text-red-500 hover:bg-red-50"
                          >
                            <Ban className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      {(r.status === "cancelled" || r.status === "done") && (
                        <button
                          onClick={() => setStatus(r.id, "confirmed")}
                          data-testid={`admin-reopen-${r.id}`}
                          title="Reopen"
                          className="rounded-full border border-pink/30 p-1.5 text-pink hover:bg-pink/5"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted">No bookings found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
